/**
 * Template slot intents (agent parity phase 2): mark, unmark and rename
 * slots (`SetLayerSlotCommand`, as the Fill panel does) and fill them by name
 * (`FillTextCommand` with the Fill panel's box growth, or
 * `ReplaceRasterCommand` with the decoded image covering the slot).
 */
import type { LayerId, SlotFillIntent, SlotMarkIntent, SlotRenameIntent, SlotUnmarkIntent } from '@maipo/agent-protocol';
import { contentLabel } from '@maipo/agent-protocol';
import { findLayer, type EditorDocument, type Layer, type TextLayer } from '../../core/model';
import { FillTextCommand, layerSlot, listSlots, ReplaceRasterCommand, SetLayerSlotCommand, slotRoleForLayer, type SlotInfo } from '../../core/template';
import { RasterTileStore } from '../../core/tiles';
import { measureTextWidth } from '../../tooling/text-measure';
import { rasterImageDecoder, resolveAsset, resolvedBlob } from '../assets';
import { defineIntent, IntentRejection, type Diagnostic, type ValidateContext } from '../registry';
import { checkUnlocked, error, layerFor, requireLayer, resolveRef, staticIds } from './common';

function slotFootprint(ids: LayerId[]) {
  return { reads: ids, writes: ids, structural: false, document: false };
}

/** The slot record exactly as the Fill panel writes it: `{ name, role, prompt? }`, in that key order. */
function slotRecord(name: string, role: SlotInfo['role'], prompt: string | undefined): SlotInfo {
  const slot: SlotInfo = { name, role };
  if (prompt) slot.prompt = prompt;
  return slot;
}

function otherSlotNamed(document: EditorDocument, name: string, except: LayerId): Layer | undefined {
  return listSlots(document).find((entry) => entry.layer.id !== except && entry.slot.name === name)?.layer;
}

export const slotMark = defineIntent<SlotMarkIntent>({
  type: 'slot.mark',
  version: 1,
  footprint: (_document, intent, aliases) => slotFootprint(staticIds([intent.layer], aliases)),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    const role = slotRoleForLayer(layer);
    if (role !== intent.role) diagnostics.push(error('UNSUPPORTED_FOR_KIND', role ? `Layer ${layer.id} can only be a ${role} slot.` : `A ${layer.kind} layer cannot be a slot (text and raster layers can).`, layer.id));
    if (otherSlotNamed(document, intent.name, layer.id)) diagnostics.push(error('COMMAND_REJECTED', `A slot named “${contentLabel(intent.name, 80)}” already exists.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => [new SetLayerSlotCommand(requireLayer(document, intent.layer, aliases).id, slotRecord(intent.name, intent.role, intent.prompt))],
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    return [{ layer: id ?? intent.layer, path: 'slot', before: layer ? layerSlot(layer) : null, after: slotRecord(intent.name, intent.role, intent.prompt) }];
  },
});

export const slotUnmark = defineIntent<SlotUnmarkIntent>({
  type: 'slot.unmark',
  version: 1,
  footprint: (_document, intent, aliases) => slotFootprint(staticIds([intent.layer], aliases)),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (layer) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => [new SetLayerSlotCommand(requireLayer(document, intent.layer, aliases).id, null)],
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    return [{ layer: id ?? intent.layer, path: 'slot', before: layer ? layerSlot(layer) : null, after: null }];
  },
});

function renamed(slot: SlotInfo, intent: SlotRenameIntent): SlotInfo {
  return slotRecord(intent.name ?? slot.name, slot.role, intent.prompt === undefined ? slot.prompt : intent.prompt);
}

export const slotRename = defineIntent<SlotRenameIntent>({
  type: 'slot.rename',
  version: 1,
  footprint: (_document, intent, aliases) => slotFootprint(staticIds([intent.layer], aliases)),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (!layerSlot(layer)) { diagnostics.push(error('COMMAND_REJECTED', `Layer ${layer.id} is not a slot; mark it first.`, layer.id)); return diagnostics; }
    if (intent.name !== undefined && otherSlotNamed(document, intent.name, layer.id)) diagnostics.push(error('COMMAND_REJECTED', `A slot named “${contentLabel(intent.name, 80)}” already exists.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    const slot = layerSlot(layer);
    if (!slot) throw new IntentRejection('COMMAND_REJECTED', `Layer ${layer.id} is not a slot.`, layer.id);
    return [new SetLayerSlotCommand(layer.id, renamed(slot, intent))];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    const slot = layer ? layerSlot(layer) : null;
    return [{ layer: id ?? intent.layer, path: 'slot', before: slot, after: slot ? renamed(slot, intent) : null }];
  },
});

/**
 * The Fill panel's text fill: a longer value grows the box (measured with
 * the editor's text measurer, +5 % and 4 px), and right- or centre-aligned
 * text shifts to keep its visual anchor, all in one FillTextCommand.
 */
export function slotTextFill(layer: TextLayer, value: string): FillTextCommand {
  const style = layer.text.style;
  const measured = Math.ceil(measureTextWidth(value, style.family, style.weight, style.size, style.tracking) * 1.05 + 4);
  const growth = Math.max(0, measured - layer.text.box.width);
  const shift = growth > 0 && style.align !== 'left' ? (style.align === 'right' ? growth : growth / 2) : 0;
  return new FillTextCommand(layer.id, value, growth, shift);
}

/** The layer of the slot named `name`; undefined when none or several have it (SetLayerSlotCommand keeps names unique, but older or imported templates may repeat one). */
function slotLayer(document: EditorDocument, name: string): Layer | undefined {
  const matches = listSlots(document).filter((entry) => entry.slot.name === name);
  return matches.length === 1 ? matches[0]?.layer : undefined;
}

function validateFill(document: EditorDocument, intent: SlotFillIntent, context: ValidateContext): Diagnostic[] {
  const layer = slotLayer(document, intent.slot);
  if (!layer) {
    const count = listSlots(document).filter((entry) => entry.slot.name === intent.slot).length;
    return [count > 1
      ? error('COMMAND_REJECTED', `${count} layers are slot “${contentLabel(intent.slot, 80)}”; rename one with slot.rename first.`)
      : error('LAYER_NOT_FOUND', `No slot is named “${contentLabel(intent.slot, 80)}”.`)];
  }
  const diagnostics: Diagnostic[] = [];
  const role = layerSlot(layer)?.role;
  if (intent.text !== undefined) {
    if (layer.kind !== 'text' || role !== 'text') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Slot “${contentLabel(intent.slot, 80)}” is an image slot; fill it with an asset.`, layer.id));
    else if (layer.text.schemaVersion === 2) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Slot “${contentLabel(intent.slot, 80)}” is portable text, which is edited in the app.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
  } else {
    if (layer.kind !== 'raster' || role !== 'image') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Slot “${contentLabel(intent.slot, 80)}” is a text slot; fill it with text.`, layer.id));
    checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
  }
  return diagnostics;
}

interface PreparedImageFill { layerId: LayerId; width: number; height: number; store: RasterTileStore }

export const slotFill = defineIntent<SlotFillIntent>({
  type: 'slot.fill',
  version: 1,
  footprint: (document, intent) => {
    const layer = slotLayer(document, intent.slot);
    return slotFootprint(layer ? [layer.id] : []);
  },
  validate: validateFill,
  prepare: async (session, intent, signal, context): Promise<PreparedImageFill | undefined> => {
    if (!intent.asset) return undefined;
    const layer = slotLayer(session.document, intent.slot);
    if (layer?.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Slot “${contentLabel(intent.slot, 80)}” is not an image slot.`);
    const resolved = await resolveAsset(intent.asset, { session, signal, assets: context.assets, decode: context.decode });
    const { width, height } = layer.raster;
    context.decode?.charge(width * height * 4, `slot.fill: ${width} × ${height} pixels`);
    const image = await rasterImageDecoder().decode(resolvedBlob(resolved), { width, height, fit: intent.fit ?? 'cover' }, signal);
    return { layerId: layer.id, width, height, store: RasterTileStore.fromImageData(image) };
  },
  needsPrepare: (intent) => intent.asset !== undefined,
  compile: (document, intent, prepared) => {
    const layer = slotLayer(document, intent.slot);
    if (!layer) throw new IntentRejection('LAYER_NOT_FOUND', `No slot is named “${contentLabel(intent.slot, 80)}”.`);
    if (intent.text !== undefined) {
      if (layer.kind !== 'text') throw new IntentRejection('UNSUPPORTED_FOR_KIND', 'Not a text slot.', layer.id);
      // An unchanged value is no edit at all, exactly as in the Fill panel.
      return layer.text.value === intent.text ? [] : [slotTextFill(layer, intent.text)];
    }
    const image = prepared as PreparedImageFill | undefined;
    if (!image) throw new IntentRejection('UNSUPPORTED_COMMAND', 'Filling an image slot prepares asynchronously.');
    if (layer.id !== image.layerId || layer.kind !== 'raster' || layer.raster.width !== image.width || layer.raster.height !== image.height) {
      throw new IntentRejection('CONFLICT', `Slot “${contentLabel(intent.slot, 80)}” changed while the image was prepared; retry.`, layer.id);
    }
    return [new ReplaceRasterCommand(layer.id, image.store, `Fill “${intent.slot}”`)];
  },
  describe: (document, intent) => {
    const layer = slotLayer(document, intent.slot);
    if (intent.text !== undefined) return [{ layer: layer?.id ?? intent.slot, path: 'text.content', before: layer?.kind === 'text' ? layer.text.value : null, after: intent.text }];
    return [{ layer: layer?.id ?? intent.slot, path: 'raster.pixels', before: null, after: 'modified' }];
  },
});
